export type ProjectTab =
    | 'domain'
    | 'requirements'
    | 'jobs'
    | 'test-cases'
    | 'gherkin'
    | 'executions'
    | 'synthetic-data-gen';

type QueryValue = string | number | boolean | null | undefined;

function appendQuery(path: string, params: URLSearchParams): string
{
    const query = params.toString();
    return query ? `${path}?${query}` : path;
}

function setQueryValue(params: URLSearchParams, key: string, value: QueryValue): void
{
    if (value === null || value === undefined || value === '')
    {
        return;
    }
    params.set(key, String(value));
}

export function projectTabRoute(
    projectId: string | number,
    tab: ProjectTab,
    extraParams?: Record<string, QueryValue>,
): string
{
    const params = new URLSearchParams();
    params.set('tab', tab);
    Object.entries(extraParams ?? {}).forEach(([key, value]) => setQueryValue(params, key, value));
    return appendQuery(`/projects/${encodeURIComponent(String(projectId))}`, params);
}

export function projectTestCasesRoute(
    projectId: string | number,
    options?: { jobId?: QueryValue; testKey?: QueryValue },
): string
{
    return projectTabRoute(projectId, 'test-cases', {
        jobId: options?.jobId,
        testKey: options?.testKey,
    });
}

export function projectExecutionsRoute(
    projectId: string | number,
    options?: { jobId?: QueryValue },
): string
{
    return projectTabRoute(projectId, 'executions', {
        jobId: options?.jobId,
    });
}

export function projectJobsRoute(
    projectId: string | number,
    options?: { jobId?: QueryValue },
): string
{
    return projectTabRoute(projectId, 'jobs', {
        jobId: options?.jobId,
    });
}

export function normalizeLegacyProjectExecutionRoute(path: string | null | undefined): string | null
{
    if (!path) return null;
    const match = path.match(/^\/projects\/([^/?#]+)\/executions(?:\/([^/?#]+))?([?#].*)?$/);
    if (!match) return path;
    return projectExecutionsRoute(decodeURIComponent(match[1]), { jobId: match[2] ? decodeURIComponent(match[2]) : undefined });
}

export function normalizeNotificationRoute(
    path: string | null | undefined,
    options?: {
        jobType?: string | null;
        status?: string | null;
        projectId?: string | number | null;
        jobId?: QueryValue;
    },
): string | null
{
    if (!path) return null;
    if (path === '/test-generations')
    {
        return options?.projectId
            ? projectJobsRoute(options.projectId, { jobId: options.jobId })
            : '/projects';
    }
    return normalizeLegacyProjectExecutionRoute(path) || path;
}

export function projectRequirementsRoute(projectId: string | number): string
{
    return projectTabRoute(projectId, 'requirements');
}
